// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Sort accidentals by +/- half-step changes
//
// Include description of object & useful info here, please!
import type { LayoutItem, LayoutSettings } from './interactivePrototype';
import InteractivePrototype from './interactivePrototype';
import utils from '../utils';
import CheckMark from './checkMark';
import ListSorter from './listSorter';

interface ScaleDegreeNamesSortLayout extends LayoutSettings {
  sortDiv?: LayoutItem;
}

const settings = {
  instructions: true,
  letters: {
    checkButton: true,
    letterButtons: false,
  },
  progressTracker: true,
  checkMark: {},
};

const layout = {
  letters: { percent: 10 },
};

class ScaleDegreeNamesSort extends InteractivePrototype {
  declare layout: ScaleDegreeNamesSortLayout;

  listDiv: HTMLDivElement;

  attempts: number = 0;

  listSorter: ListSorter;

  // Public constructor
  constructor(displayDiv, options) {
    super(displayDiv, options);
    this.targetSpeedFromSkills = ScaleDegreeNamesSort.registry.skills;
    this.settings = utils.clone(settings);
    this.layout = utils.clone(layout);
    this.init(displayDiv, options);
  }

  init(displayDiv, options) {
    // now call the prototype's init function:
    return super.init(displayDiv, options).then(() => {
      this.progressTracker.start(this.targetSpeed);
      this.letterPanel.addCheckCallback(this.checkAnswer, this);
      this.attempts = 0;
    });
  }

  displayInstructions() {
    this.instructionsDiv.innerHTML = 'Drag & sort the names to match the scale degrees.';
  }

  createCustomDivs() {
    this.createDiv('listDiv', { width: '100%', height: 'auto' });
    this.displayDiv.removeChild(this.listDiv);
    this.displayDiv.insertBefore(this.listDiv, this.lettersDiv);
    this.checkMark = new CheckMark(this.listDiv);
  }

  initializeCustomElements() {
    const scaleDegrees = [
      'Scale Degree:',
      { classes: 'accidentals', label: '1' },
      { classes: 'accidentals', label: '2' },
      { classes: 'accidentals', label: '3' },
      { classes: 'accidentals', label: '4' },
      { classes: 'accidentals', label: '5' },
      { classes: 'accidentals', label: '6' },
      { classes: 'accidentals', label: '7' },
      '<span class="accidentals">7</span> <small>(nat. minor)</small>',
    ];

    const scaleDegreeNames = [
      'Name:',
      'Tonic',
      'Supertonic',
      'Mediant',
      'Subdominant',
      'Dominant',
      'Submediant',
      'Leading Tone',
      'Subtonic',
    ];
    const lists = [scaleDegrees, scaleDegreeNames];
    this.listSorter = new ListSorter(this.listDiv, lists);
    this.layout.sortDiv = {
      div: this.listDiv,
      visible: true,
      percent: 85,
      callback: this.listSorter.resize.bind(this.listSorter),
    };
  }

  gameOver() {
    this.lettersDiv.style.display = 'none';
    this.listSorter.gameOver();
    super.gameOver();
  }

  exit() {
    this.listSorter?.gameOver();
    super.exit();
  }

  checkAnswer(rightBoolean) {
    this.attempts++;
    let answers = this.listSorter.check();
    if (typeof rightBoolean !== 'undefined') {
      answers = rightBoolean ? { right: 10, wrong: 0 } : { wrong: 10, right: 0 };
    }
    const gameOver = answers.wrong === 0;
    this.progressTracker.multipleAnswers(
      [
        {
          skill: 'scale_degree_names',
          right: answers.right,
          wrong: answers.wrong,
          questions: answers.right + answers.wrong,
        },
      ],
      answers.right + answers.wrong,
      gameOver
    );
    if (gameOver) {
      this.checkMark.check(2);
      this.gameOver();
    } else {
      this.checkMark.x();
      if (this.attempts > 1) this.listSorter.hint();
    }
  }

  debugRightAnswer() {
    this.checkAnswer(true);
  }

  debugWrongAnswer() {
    this.checkAnswer(false);
  }

  static registry = {
    description: 'Match scale degrees & their names',
    skills: ['scale_degree_names'],
  };
}

export default ScaleDegreeNamesSort;
